import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link2 } from "lucide-react";
import { toast } from "sonner";

const OAUTH_HINTS = {
  access_denied: "Google blocked access. Add this Google account under Google Cloud → Google Auth Platform → Audience → Test users, then try again.",
  redirect_uri_mismatch: "Redirect URI mismatch — add this app's callback URL to the OAuth client in Google Cloud → Clients.",
  no_refresh_token: "Google didn't return a refresh token. Remove Auto-Close-Ai at myaccount.google.com/permissions and connect again.",
  token_exchange_failed: "Google rejected the code exchange — check the Client Secret saved in Auto-Close.",
  no_email_on_profile: "Google didn't share the account email — make sure the userinfo.email scope is allowed.",
};

export default function OAuthConnect({ onConnected }) {
  const [providers, setProviders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => { api.get("/oauth/providers").then((r) => setProviders(r.data)); }, []);
  useEffect(() => {
    const connected = params.get("connected");
    const err = params.get("oauth_error");
    if (connected) { toast.success(`${connected === "gmail" ? "Gmail & Google Calendar" : "Outlook"} connected`, { id: "oauth-ok" }); onConnected?.(); }
    if (err) toast.error(OAUTH_HINTS[err] || `Connection failed: ${err.replace(/_/g, " ")}`, { id: "oauth-error", duration: 12000 });
    if (connected || err) setParams({}, { replace: true });
  }, [params, setParams, onConnected]);

  const connect = async (p) => {
    setBusy(p);
    try {
      const { data } = await api.get(`/oauth/${p}/start`);
      if (window.self !== window.top) {
        const w = window.open(data.authorization_url, "_blank");
        if (w) w.opener = null;
        if (!w) { toast.error("Your browser blocked the Google popup — open the app in its own tab and try again."); }
        else { toast.info("Google sign-in opened in a new tab — finish there, then come back and refresh.", { duration: 10000 }); }
        setBusy(null);
        return;
      }
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(null);
    }
  };

  if (!providers) return null;
  const item = (p, label, color) => {
    const ready = providers[p];
    return (
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border last:border-0" data-testid={`oauth-${p}`}>
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${ready ? color : "bg-muted-foreground/40"}`} />{label}</div>
          <div className="text-xs text-muted-foreground mono truncate">
            {ready ? "One-click connect · sends via API · replies auto-detected" : `Not configured — set ${p === "gmail" ? "GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET" : "MS_CLIENT_ID / MS_CLIENT_SECRET"} in backend .env · redirect: ${providers.redirect_uris?.[p]}`}
          </div>
        </div>
        <Button size="sm" variant={ready ? "default" : "outline"} disabled={!ready || busy === p} onClick={() => connect(p)} data-testid={`oauth-connect-${p}`}>
          <Link2 className="w-3.5 h-3.5 mr-1.5" />{busy === p ? "Redirecting..." : `Connect ${label}`}
        </Button>
      </div>
    );
  };
  return (
    <div className="border-b border-border" data-testid="oauth-connect">
      {item("gmail", "Gmail", "bg-rose-500")}
      {item("outlook", "Outlook", "bg-sky-500")}
    </div>
  );
}
